import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Flag from "react-world-flags";
import BilingualView from "@/components/BilingualView";
import ReportErrorDialog from "@/components/ReportErrorDialog";
import SEO from "@/components/SEO";
import AdSlot from "@/components/AdSlot";
import RelatedSpeeches from "@/components/RelatedSpeeches";
import ShareButtons from "@/components/ShareButtons";
import YouTubeGate from "@/components/YouTubeGate";
import { formatDateIt } from "@/lib/date";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  ExternalLink, MapPin, Calendar, Sparkles, Loader2, ChevronLeft, ListChecks, Languages,
} from "lucide-react";

export default function Article() {
  const { id } = useParams();
  const [speech, setSpeech] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [playKey, setPlayKey] = useState(0);
  const [startAt, setStartAt] = useState(null);
  const videoRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/speeches/${id}`).then((r) => setSpeech(r.data)).catch(() => setNotFound(true));
  }, [id]);

  const handleSeek = (seconds) => {
    setStartAt(seconds);
    setPlayKey((k) => k + 1);
    if (videoRef.current) {
      videoRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-24 text-center">
        <SEO title="Discorso non trovato" noindex suppressCanonical />
        <h1 className="font-serif text-3xl font-bold mb-3">Discorso non trovato</h1>
        <Link to="/archivio" className="text-primary hover:underline">Torna all'archivio</Link>
      </div>
    );
  }

  if (!speech) {
    return <div className="py-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  // Titolo SEO breve: mantiene il <title> completo entro ~60 caratteri (con il suffisso "| Smart News Italia")
  const SEO_TITLE_MAX = 40;
  const seoTitle = speech.title.length > SEO_TITLE_MAX ? `${speech.title.slice(0, SEO_TITLE_MAX - 1).trim()}…` : speech.title;
  const summaryText = speech.summary?.[0] || "discorso ufficiale tradotto in italiano";
  let seoDesc = `${speech.leader}${speech.event_date ? `, ${formatDateIt(speech.event_date)}` : ""}: ${summaryText}. Video originale, testo integrale, traduzione italiana e fonte ufficiale.`;
  if (seoDesc.length > 300) seoDesc = `${seoDesc.slice(0, 297).trim()}…`;
  const ogImg = speech.youtube_id ? `https://img.youtube.com/vi/${speech.youtube_id}/maxresdefault.jpg` : undefined;
  const canonicalPath = `/discorso/${speech.id}`;

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://smartnewsitalia.it/" },
      { "@type": "ListItem", position: 2, name: "Archivio", item: "https://smartnewsitalia.it/archivio" },
      { "@type": "ListItem", position: 3, name: speech.title, item: `https://smartnewsitalia.it${canonicalPath}` },
    ],
  };
  const videoLd = speech.youtube_id
    ? {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: speech.title,
        description: seoDesc,
        thumbnailUrl: `https://img.youtube.com/vi/${speech.youtube_id}/maxresdefault.jpg`,
        uploadDate: speech.event_date || undefined,
        embedUrl: `https://www.youtube.com/embed/${speech.youtube_id}`,
        inLanguage: "it",
      }
    : null;
  const jsonLd = videoLd ? [videoLd, breadcrumbLd] : [breadcrumbLd];

  return (
    <article className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
      <SEO
        title={seoTitle}
        description={seoDesc}
        image={ogImg}
        imageWidth={ogImg ? 1280 : undefined}
        imageHeight={ogImg ? 720 : undefined}
        imageAlt={`${speech.leader} — ${speech.title}`}
        type="article"
        canonicalPath={canonicalPath}
        jsonLd={jsonLd}
      />
      <Link to="/archivio" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-6" data-testid="back-to-archive">
        <ChevronLeft className="h-4 w-4" /> Archivio
      </Link>

      {/* Header */}
      <header className="mb-8">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          {speech.country_code && (
            <span className="w-8 h-5 overflow-hidden rounded-[2px] border border-border">
              <Flag code={speech.country_code} className="w-full h-full object-cover" fallback={<span />} />
            </span>
          )}
          <span className="text-sm font-medium">{speech.leader}</span>
          {speech.theme && <Badge variant="secondary" className="rounded-sm">{speech.theme}</Badge>}
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary">
            <Languages className="h-3.5 w-3.5" /> Traduzione e doppiaggio in italiano
          </span>
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight tracking-tight">
          {speech.title}
        </h1>
        <div className="flex flex-wrap gap-4 mt-4 text-sm text-muted-foreground">
          {speech.event_date && (
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4" /> <span>{formatDateIt(speech.event_date)}</span></span>
          )}
          {speech.event_location && (
            <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {speech.event_location}</span>
          )}
        </div>
      </header>

      {/* Video embed (gated by consent) */}
      {speech.youtube_id && (
        <div ref={videoRef}>
          <YouTubeGate youtubeId={speech.youtube_id} title={speech.title} playKey={playKey} startAt={startAt} />
        </div>
      )}
      {speech.paragraphs?.some((p) => p.timestamp) && (
        <p className="text-xs text-muted-foreground mb-6">
          Seleziona un minuto nel testo per avviare il video dal passaggio corrispondente.
        </p>
      )}

      {/* Condivisione social sotto il video */}
      <div className="mb-8 pb-6 border-b border-border">
        <ShareButtons title={speech.title} compact />
      </div>

      {/* AI transparency badge */}
      <div className="flex items-start gap-3 bg-muted/60 border border-border rounded-sm p-4 mb-10 text-sm">
        <Sparkles className="h-4 w-4 text-primary mt-0.5 shrink-0" />
        <p className="text-muted-foreground leading-relaxed">
          <span className="font-semibold text-foreground">Avviso:</span> traduzione, doppiaggio ed
          eventuale sincronizzazione labiale sono realizzati con strumenti di intelligenza
          artificiale. Il contenuto del discorso non viene riscritto e il significato delle
          dichiarazioni non viene modificato. Consulta sempre il video e la fonte originale.
        </p>
      </div>

      {/* AI Summary */}
      {speech.summary?.length > 0 && (
        <section className="mb-10">
          <h2 className="flex items-center gap-2 font-serif text-2xl font-bold mb-4">
            <ListChecks className="h-5 w-5 text-primary" /> I temi affrontati
          </h2>
          <ul className="space-y-2.5" data-testid="summary-list">
            {speech.summary.map((point, i) => (
              <li key={i} className="flex gap-3 text-base leading-relaxed">
                <span className="text-primary font-mono text-sm mt-1 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Separator className="my-10" />

      <AdSlot placement="article_inline" className="mb-10" />

      {/* Bilingual view */}
      <section className="mb-10">
        <h2 className="font-serif text-2xl font-bold mb-2">Il discorso, parola per parola</h2>
        <p className="text-sm text-muted-foreground mb-6">
          Il testo originale e la traduzione italiana sono allineati passaggio per passaggio.
          Seleziona un minuto per raggiungere quel punto nel video.
        </p>
        <BilingualView paragraphs={speech.paragraphs} originalLanguage={speech.original_language} onSeek={speech.youtube_id ? handleSeek : undefined} />
      </section>

      {/* Trust layer */}
      <section className="bg-card border border-border rounded-sm p-6">
        <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-4">
          Fonte, data e luogo
        </h3>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1 text-sm">
            {speech.source_url ? (
              <a
                href={speech.source_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="source-link"
                className="inline-flex items-center gap-1.5 text-primary font-medium hover:underline"
              >
                <ExternalLink className="h-4 w-4" />
                Guarda la fonte ufficiale{speech.source_name ? ` — ${speech.source_name}` : ""}
              </a>
            ) : (
              <span className="text-muted-foreground">Fonte non specificata</span>
            )}
            {speech.event_location && (
              <p className="text-muted-foreground">{speech.event_location} · {formatDateIt(speech.event_date)}</p>
            )}
          </div>
          <ReportErrorDialog speechId={speech.id} />
        </div>
      </section>

      <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-border pt-8">
        <ShareButtons title={speech.title} />
        <Link to="/metodologia" className="text-sm text-primary hover:underline">
          Scopri il nostro metodo →
        </Link>
      </div>

      <RelatedSpeeches speechId={speech.id} />
    </article>
  );
}
